/**
 * VoiceDisclaimerModal — one-time disclaimer pro modo voice
 * (web /ia, Fase Voice S2).
 *
 * Espelha apps/extension/src/sidebar/components/ai/VoiceDisclaimerModal.tsx,
 * adaptado para web:
 *   - localStorage (síncrono) em vez de chrome.storage.local (async)
 *   - Strings via prop `t` (5 idiomas)
 *
 * Disparado na primeira vez que tier especialista/unlimited clica VOZ.
 * Persiste aceite em localStorage key 'drpera_voice_disclaimer_accepted'.
 */
import { useCallback, type FC } from 'react';

/* ------------------------------------------------------------------------- */
/* Storage helpers (síncronos — web usa localStorage)                          */
/* ------------------------------------------------------------------------- */

/** Key do localStorage — prefixo `drpera_` igual a outros consents/locale. */
export const VOICE_DISCLAIMER_KEY = 'drpera_voice_disclaimer_accepted';

export function carregarVoiceDisclaimer(): boolean {
  try {
    return localStorage.getItem(VOICE_DISCLAIMER_KEY) === '1';
  } catch {
    return false;
  }
}

export function salvarVoiceDisclaimer(): void {
  try {
    localStorage.setItem(VOICE_DISCLAIMER_KEY, '1');
  } catch {
    /* ignore — disclaimer aparece de novo na próxima */
  }
}

/* ------------------------------------------------------------------------- */
/* Componente                                                                  */
/* ------------------------------------------------------------------------- */

export interface VoiceDisclaimerModalProps {
  /** Aceito — pai recebe sinal pra abrir pareamento. Modal grava storage antes. */
  readonly onAccept: () => void;
  /** Cancelado — modal NÃO grava storage (próxima vez aparece de novo). */
  readonly onCancel: () => void;
  /** Função de tradução do useTranslations. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

const VoiceDisclaimerModal: FC<VoiceDisclaimerModalProps> = ({ onAccept, onCancel, t }) => {
  const handleAccept = useCallback((): void => {
    salvarVoiceDisclaimer();
    onAccept();
  }, [onAccept]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-disclaimer-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        background: 'rgba(34,37,50,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          maxHeight: '92vh',
          background: 'var(--ink-soft)',
          border: '1px solid var(--violet)',
          boxShadow: '0 0 24px rgba(124,124,248,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid rgba(124,124,248,0.30)',
            background: 'var(--ink)',
            flexShrink: 0,
          }}
        >
          <h2
            id="voice-disclaimer-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.04em',
            }}
          >
            🎙 {t('voice.disclaimer.title')}
          </h2>
        </div>

        {/* Body — 3 seções (How / Responsibility / Consumption) */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 14,
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            color: 'var(--ink-light)',
            lineHeight: 1.6,
          }}
        >
          {/* Seção 1 — Como funciona */}
          <section style={{ marginBottom: 14 }}>
            <p
              style={{
                margin: '0 0 6px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--violet)',
                textShadow: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('voice.disclaimer.section_how')}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>{t('voice.disclaimer.how_1')}</li>
              <li>{t('voice.disclaimer.how_2')}</li>
              <li>{t('voice.disclaimer.how_3')}</li>
              <li>{t('voice.disclaimer.how_4')}</li>
              <li>{t('voice.disclaimer.how_5')}</li>
            </ul>
          </section>

          {/* Seção 2 — Responsabilidade médica */}
          <section style={{ marginBottom: 14 }}>
            <p
              style={{
                margin: '0 0 6px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--violet)',
                textShadow: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('voice.disclaimer.section_responsibility')}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>{t('voice.disclaimer.resp_1')}</li>
              <li>{t('voice.disclaimer.resp_suggestion')}</li>
              <li>{t('voice.disclaimer.resp_cfm')}</li>
            </ul>
          </section>

          {/* Seção 3 — Consumo de créditos */}
          <section>
            <p
              style={{
                margin: '0 0 6px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--violet)',
                textShadow: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('voice.disclaimer.section_consumption')}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>{t('voice.disclaimer.cons_1')}</li>
              <li>{t('voice.disclaimer.cons_2')}</li>
              <li>{t('voice.disclaimer.cons_3')}</li>
            </ul>
          </section>
        </div>

        {/* Footer — 2 botões */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '12px 14px',
            borderTop: '1px solid rgba(124,124,248,0.30)',
            background: 'var(--ink)',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              color: 'var(--ink-light)',
              border: '1px solid rgba(124,124,248,0.40)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              borderRadius: 0,
              boxShadow: 'none',
              transition: 'border-color 180ms ease, box-shadow 180ms ease, color 180ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.borderColor = '#FF6A00';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'rgba(124,124,248,0.40)';
              e.currentTarget.style.color = 'var(--ink-light)';
            }}
          >
            {t('voice.disclaimer.cancel')}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            style={{
              flex: 2,
              padding: '10px 12px',
              background: 'var(--cta-blue)',
              color: '#FFFFFF',
              border: '1px solid var(--cta-blue)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              borderRadius: 0,
              boxShadow: 'none',
              transition: 'box-shadow 180ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.borderColor = '#FF6A00';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'var(--cta-blue)';
            }}
          >
            {t('voice.disclaimer.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceDisclaimerModal;
